import {memo} from "react";
import {Badge, Button, Card, Divider, Space, Typography} from "antd";
import {ReloadOutlined, TrophyOutlined} from "@ant-design/icons";
import PropTypes from "prop-types";

const {Text} = Typography;

const WinnersPanel = ({winners, maxWinners, refreshing, refreshDisabled, onRefresh, onShowResults}) => (
    <Card
        className="h-full flex flex-col"
        styles={{
            header: {
                background: 'linear-gradient(90deg, rgba(169, 118, 42, 0.55), rgba(232, 193, 112, 0.22))',
                color: 'var(--draw-gold-bright)',
                borderRadius: '8px 8px 0 0',
                flexShrink: 0
            },
            // Let the body own the leftover height so a full winners list scrolls
            // inside the card instead of overflowing it on a short screen.
            body: {
                flex: 1,
                minHeight: 0,
                overflowY: 'auto'
            }
        }}
        title={
            <div className="flex items-center justify-between">
                <Space>
                    <TrophyOutlined/>
                    <span>Урамшууллын эзэд</span>
                    <Badge count={winners.length} style={{backgroundColor: 'var(--draw-gold-deep)', color: '#FFF3D6'}}/>
                </Space>
                <Button
                    type="text"
                    size="small"
                    icon={<ReloadOutlined/>}
                    loading={refreshing}
                    disabled={refreshDisabled}
                    onClick={onRefresh}
                    // Dimmed explicitly: the inline colour would otherwise override
                    // AntD's disabled colour and leave it looking clickable.
                    style={{color: refreshDisabled ? 'var(--draw-text-faint)' : 'var(--draw-gold-bright)'}}
                    title={refreshDisabled ? 'Эргэлт дуустал шинэчлэх боломжгүй' : 'Серверээс шинэчлэх'}
                    aria-label="Серверээс шинэчлэх"
                />
            </div>
        }
    >
        <div className="space-y-3 overflow-y-auto">
            {winners.length === 0 ? (
                <div className="text-center py-8" style={{color: 'var(--draw-text-faint)'}}>
                    <TrophyOutlined aria-hidden="true" style={{fontSize: '48px', marginBottom: '16px'}}/>
                    <Text>Урамшуулал эхлээгүй байна</Text>
                </div>
            ) : (
                <ol className="list-none p-0 m-0 space-y-3">
                    {winners.map((winner, index) => (
                        <li
                            key={winner.custno ?? `winner-${index}`}
                            className="flex items-center gap-3 p-3 rounded-lg animate-fade-in"
                            style={{
                                animationDelay: `${index * 0.2}s`,
                                background: 'linear-gradient(90deg, rgba(232, 193, 112, 0.18), rgba(232, 193, 112, 0.06))',
                                border: '1px solid var(--draw-line)',
                            }}
                        >
                            <TrophyOutlined aria-hidden="true" style={{color: 'var(--draw-gold)', fontSize: '14px'}}/>
                            <div className="flex-1">
                                <Text strong className="text-md" style={{color: 'var(--draw-gold-bright)'}}>
                                    {index + 1} - {winner.name}
                                </Text>
                                <div className="text-sm font-bold" style={{color: 'var(--draw-text)'}}>{winner.regnum}</div>
                            </div>
                        </li>
                    ))}
                </ol>
            )}

            {winners.length > 0 && winners.length < maxWinners && (
                <Divider>
                    <Text type="secondary">{maxWinners - winners.length} байр үлдсэн</Text>
                </Divider>
            )}

            {winners.length >= maxWinners && (
                <button
                    onClick={onShowResults}
                    className="draw-gold-button w-52 h-12 rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    style={{outlineColor: 'var(--draw-gold-bright)'}}
                >
                    Урамшууллын эздийг харах
                </button>
            )}
        </div>
    </Card>
);

WinnersPanel.propTypes = {
    winners: PropTypes.array.isRequired,
    maxWinners: PropTypes.number.isRequired,
    refreshing: PropTypes.bool,
    refreshDisabled: PropTypes.bool,
    onRefresh: PropTypes.func.isRequired,
    onShowResults: PropTypes.func.isRequired,
};

export default memo(WinnersPanel);
